import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  DollarSign, CalendarClock, Layers, User, CheckCircle2, Send, PlayCircle, ArrowLeft, Pencil, Trash2, Upload, RefreshCw, Loader2, Box, Plus, Receipt, BadgeCheck, Download, Eye, FolderDown, Image as ImageIcon, Sparkles,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  getCase, listApplications, getApplication, applyToCase, selectDesigner, updateStatus, updateCase, deleteCase, deleteOwnCase, getCasePrivateOwner, getCasePrivateShared, changeDesigner, acceptCaseConfidentiality,
} from "../services/cases";
import { listSubmissions } from "../services/submissions";
import { listChangeRequests } from "../services/changeRequests";
import { StlViewer } from "../components/StlViewer";
import { Odontogram } from "../components/Odontogram";
import { getCaseRestorations, isImplantType, teethList, toothLabel } from "../lib/dental";
import { CaseComments } from "../components/CaseComments";
import { TranslatableText } from "../components/TranslatableText";
import { SubmissionVersions } from "../components/SubmissionVersions";
import { CaseReview } from "../components/CaseReview";
import { RatingSummary } from "../components/StarRating";
import { DesignerProfileModal } from "../components/DesignerProfileModal";
import { getRatingSummary } from "../services/reviews";
import { getCommissionRate } from "../services/settings";
import { acceptPayoutPolicy } from "../services/profiles";
import { getUser } from "../services/profiles";
import { matchDesignersForCase, caseToMeta, designerToMeta } from "../services/aiMatch";
import PayoutPolicyDialog from "../components/PayoutPolicyDialog";
import { PAYOUT_POLICY_VERSION } from "../context/AuthContext";
import { createCheckout, getPayoutStatement, requestPayoutInfo, validatePromo } from "../services/payments";
import { fromLabPrice, fromDesiredNet, fmtUSD, caseOfferedPrice } from "../lib/pricing";
import { getCaseCadFiles, isCadFile } from "../lib/cad";
import { uploadFileResumable, downloadCaseFile } from "../services/storage";
import { AiAnalysisPanel } from "../components/AiAnalysisPanel";
import { CaseStatusTimeline } from "../components/CaseStatusTimeline";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../components/ui/dialog";

const DetailItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2.5">
    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary text-muted-foreground">
      <Icon className="h-4 w-4" />
    </div>
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{label}</p>
      <p className="text-sm font-medium">{value || "—"}</p>
    </div>
  </div>
);

const RestorationCard = ({ restoration, index, numbering }) => {
  const { t } = useTranslation();
  const teeth = teethList(restoration.teeth);
  const showImplant = isImplantType(restoration.type) && restoration.implant &&
    Object.values(restoration.implant).some((v) => v);
  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid={`restoration-detail-${index}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-brand/10 px-2.5 py-0.5 text-xs font-semibold text-brand">{t("restoration.item", { n: index + 1 })}</span>
        <span className="font-heading text-base font-semibold tracking-tight">{t(`case.restorationTypes.${restoration.type}`, restoration.type)}</span>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <DetailItem icon={Layers} label={t("case.material")} value={t(`case.materials.${restoration.material}`, restoration.material)} />
        <DetailItem icon={Layers} label={t("case.shade")} value={restoration.shade} />
        <DetailItem icon={Layers} label={t("restoration.teeth")} value={teeth.map((x) => toothLabel(x.fdi, numbering)).join(", ") || "—"} />
      </div>
      {teeth.length > 0 && (
        <div className="mt-4">
          <Odontogram value={restoration.teeth} numbering={numbering} readOnly testid={`odontogram-view-${index}`} />
        </div>
      )}
      {showImplant && (
        <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50/60 p-3" data-testid={`implant-detail-${index}`}>
          <p className="mb-2 text-sm font-semibold text-sky-900">{t("restoration.implantInfo")}</p>
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
            {restoration.implant.retainedBy && <DetailItem icon={Layers} label={t("restoration.retainedBy")} value={t(`restoration.${restoration.implant.retainedBy}`, restoration.implant.retainedBy)} />}
            {restoration.implant.brand && <DetailItem icon={Layers} label={t("restoration.brand")} value={restoration.implant.brand} />}
            {restoration.implant.connection && <DetailItem icon={Layers} label={t("restoration.connection")} value={restoration.implant.connection} />}
            {restoration.implant.tiBase && <DetailItem icon={Layers} label={t("restoration.tiBase")} value={restoration.implant.tiBase} />}
            {restoration.implant.scanbody && <DetailItem icon={Layers} label={t("restoration.scanbody")} value={restoration.implant.scanbody} />}
          </div>
        </div>
      )}
      {restoration.clinicalInstructions && (
        <div className="mt-4 border-t border-border pt-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t("case.instructions")}</p>
          <TranslatableText text={restoration.clinicalInstructions} testid={`restoration-instructions-${index}`} />
        </div>
      )}
    </div>
  );
};

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, profile, refreshProfile } = useAuth();
  const [caseItem, setCaseItem] = useState(null);
  const [applications, setApplications] = useState([]);
  const [applicantRatings, setApplicantRatings] = useState({});
  const [myApp, setMyApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [counts, setCounts] = useState({ versions: 0, changes: 0 });
  const [dlBusy, setDlBusy] = useState("");
  const [aiCmpBusy, setAiCmpBusy] = useState(false);
  const [aiCmpMap, setAiCmpMap] = useState({});
  const [aiCmpIntro, setAiCmpIntro] = useState(false);
  const [profileModal, setProfileModal] = useState(null); // { designerId, app }
  const [cadIdx, setCadIdx] = useState(0);
  const [cadBusy, setCadBusy] = useState(false);
  const [casePrivate, setCasePrivate] = useState(null);
  const [confidentialityOpen, setConfidentialityOpen] = useState(false);
  const [confidentialityChecked, setConfidentialityChecked] = useState(false);
  const [commissionRate, setCommissionRate] = useState(0.15);
  const [counterOn, setCounterOn] = useState(false);
  const [desiredNet, setDesiredNet] = useState("");
  const [payout, setPayout] = useState(null); // designer-safe payout status for this case
  const [promoInput, setPromoInput] = useState("");
  const [promoResult, setPromoResult] = useState(null); // {valid, reason, uses, max, remaining}
  const [promoBusy, setPromoBusy] = useState(false);

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoBusy(true);
    try {
      const res = await validatePromo(code, user.uid);
      setPromoResult(res);
      if (res.valid) toast.success(t("promo.validMsg"));
      else toast.error(t(`promo.${res.reason === "expired" ? "expiredMsg" : res.reason === "exhausted" ? "exhaustedMsg" : "invalidMsg"}`));
    } catch (e) {
      toast.error(t("promo.invalidMsg"));
    } finally {
      setPromoBusy(false);
    }
  };

  const removePromo = () => { setPromoResult(null); setPromoInput(""); };
  const [payoutGateOpen, setPayoutGateOpen] = useState(false);
  const [payoutGateBusy, setPayoutGateBusy] = useState(false);

  const hasAcceptedPayoutPolicy = !!profile?.payoutPolicy?.accepted;

  // First-application gate: designers who never accepted the payout policy must do so
  // once, before their first application. Returns true if the apply flow may proceed.
  const ensurePayoutPolicy = () => {
    if (hasAcceptedPayoutPolicy) return true;
    setPayoutGateOpen(true);
    return false;
  };

  const acceptPayoutGate = async () => {
    setPayoutGateBusy(true);
    try {
      await acceptPayoutPolicy(user.uid, PAYOUT_POLICY_VERSION);
      await refreshProfile();
      setPayoutGateOpen(false);
      setApplyOpen(true);
    } catch (e) {
      toast.error(e?.message || "Failed");
    } finally {
      setPayoutGateBusy(false);
    }
  };

  useEffect(() => { getCommissionRate().then(setCommissionRate).catch(() => {}); }, []);

  // Sync payout status from the backend (source of truth: Mongo payment_transactions).
  // This lets the designer's case immediately reflect "Paid" once the admin marks it,
  // even though the payout is not written back to the Firestore case document.
  useEffect(() => {
    const sid = caseItem?.paymentTransactionId;
    if (caseItem?.paymentStatus === "paid" && sid) {
      getPayoutStatement(sid)
        .then((p) => {
          setPayout(p);
          // When the case is approved (Completed) and payout is still pending, ask the
          // backend to email the designer for payout instructions. Idempotent server-side.
          if (caseItem?.status === "Completed" && p?.payout_status !== "paid") {
            requestPayoutInfo(sid).catch(() => {});
          }
        })
        .catch(() => setPayout(null));
    } else {
      setPayout(null);
    }
  }, [caseItem?.paymentTransactionId, caseItem?.paymentStatus, caseItem?.status]);

  const isOwner = caseItem && user && caseItem.ownerId === user.uid;
  // P5 (fixed): owner may permanently delete a case as long as there is NO confirmed
  // payment — regardless of status (Draft/Published), applications, or a selected
  // designer. Once a payment is confirmed, deletion is blocked to preserve financials.
  const canDeleteOwnCase = isOwner && caseItem
    && caseItem.paymentStatus !== "paid"
    && caseItem.payoutStatus !== "paid";
  const isSelectedDesigner = caseItem && user && caseItem.selectedDesignerId === user.uid;
  // Only the case owner (lab) and the selected designer may DOWNLOAD the case's
  // clinical/STL files. Non-selected designers (marketplace/applied) can preview the
  // 3D model but never download. The backend enforces this too (signed URL is gated).
  const canDownloadFiles = isOwner || isSelectedDesigner;

  const handleFileDownload = async (ref, name) => {
    setDlBusy(ref);
    try {
      await downloadCaseFile(id, ref, name);
    } catch (e) {
      const s = e?.response?.status;
      toast.error(s === 403 ? t("files.notAuthorized") : t("files.downloadFailed"));
    } finally {
      setDlBusy("");
    }
  };

  // Lab: AI recommendation comparing the designers who applied to THIS case. Only
  // NON-PHI profile/offer metadata is sent. AI never selects — the lab decides.
  const runCompareApplicants = async () => {
    if (aiCmpBusy || applications.length < 2) return;
    setAiCmpIntro(false);
    setAiCmpBusy(true);
    try {
      const rank = {};
      const profiles = await Promise.all(applications.map((a) => getUser(a.designerId).catch(() => null)));
      const candidates = applications.map((a, i) => ({
        id: a.designerId,
        ...designerToMeta(profiles[i]?.designerProfile || {}, {
          proposedPrice: a.labPrice ?? caseOfferedPrice(caseItem) ?? null,
        }),
      }));
      const matches = await matchDesignersForCase(caseToMeta(caseItem), candidates, i18n.language?.split("-")[0] || "en");
      matches.forEach((m, idx) => { rank[m.id] = { ...m, rank: idx + 1 }; });
      setAiCmpMap(rank);
      if (!matches.length) toast.error(t("aiMatch.unavailable"));
    } catch {
      toast.error(t("aiMatch.unavailable"));
    } finally {
      setAiCmpBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const c = await getCase(id);
      setCaseItem(c);
      if (c && user) {
        // Secondary data (applications) is non-critical: never let it hide the case.
        try {
          if (c.ownerId === user.uid) {
            const apps = await listApplications(id);
            setApplications(apps);
            const entries = await Promise.all(
              apps.map(async (a) => [a.designerId, await getRatingSummary(a.designerId)])
            );
            setApplicantRatings(Object.fromEntries(entries));
          } else if (profile?.role === "designer") {
            setMyApp(await getApplication(id, user.uid));
          }
          // Timeline detail: version + change-request counts (participants only).
          if (c.ownerId === user.uid || c.selectedDesignerId === user.uid) {
            const [subs, crs] = await Promise.all([listSubmissions(id), listChangeRequests(id)]);
            setCounts({ versions: subs.length, changes: crs.length });
            // Confidential data: the owner sees everything; the selected designer sees
            // ONLY what the lab chose to share.
            if (c.ownerId === user.uid) setCasePrivate(await getCasePrivateOwner(id));
            else setCasePrivate(await getCasePrivateShared(id));
          }
        } catch {
          /* ignore */
        }
      }
    } catch (e) {
      setCaseItem(null);
    } finally {
      setLoading(false);
    }
  }, [id, user, profile]);

  useEffect(() => { load(); }, [load]);

  const handleApply = async () => {
    const offered = caseOfferedPrice(caseItem);
    let pricing;
    if (counterOn) {
      const net = Number(desiredNet);
      if (!net || net <= 0) {
        toast.error(t("pricing.desiredNet"));
        return;
      }
      pricing = { offerType: "counter", ...fromDesiredNet(net, commissionRate) };
    } else {
      pricing = { offerType: "accept", ...fromLabPrice(offered, commissionRate) };
    }
    setBusy(true);
    try {
      await applyToCase(id, {
        designerId: user.uid,
        name: profile?.name || user.email,
        email: user.email,
        message,
        ...pricing,
      });
      toast.success(t("applications.applied"));
      setApplyOpen(false);
      setCounterOn(false);
      setDesiredNet("");
      await load();
    } catch (e) {
      toast.error(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const handleSelect = async (app) => {
    const offered = caseOfferedPrice(caseItem);
    // Use the applicant's own offer; fall back to the offered price for legacy apps.
    const agreement = app.labPrice
      ? { labPrice: app.labPrice, designerNet: app.designerNet, commissionRate: app.commissionRate, commissionAmount: app.commissionAmount, offerType: app.offerType || "accept" }
      : { ...fromLabPrice(offered, commissionRate), offerType: "accept" };
    setBusy(true);
    try {
      await selectDesigner(id, app, agreement);
      toast.success(t("actions.designerSelected"));
      await load();
    } catch (e) {
      toast.error(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const handlePay = async () => {
    const rate = caseItem.agreedCommissionRate ?? commissionRate;
    const labPrice = caseItem.agreedLabPrice ?? caseOfferedPrice(caseItem);
    setBusy(true);
    try {
      const { checkout_url } = await createCheckout({
        case_id: id,
        case_number: caseItem.caseNumber || "",
        case_title: caseItem.title || "",
        amount: labPrice,
        currency: "usd",
        lab_id: user.uid,
        lab_name: profile?.name || user.email,
        designer_id: caseItem.selectedDesignerId || "",
        designer_name: caseItem.selectedDesignerName || "",
        lab_email: user.email || "",
        designer_email: caseItem.selectedDesignerEmail || "",
        commission_rate: rate,
        offer_type: caseItem.agreedOfferType || "accept",
        promo_code: promoResult?.valid ? "LAUNCH50" : "",
        origin_url: window.location.origin,
      });
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("payment.checkoutError"));
      setBusy(false);
    }
  };

  const changeStatus = async (status) => {
    setBusy(true);
    try {
      await updateStatus(id, status);
      toast.success(t("actions.statusUpdated"));
      await load();
    } catch (e) {
      toast.error(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t("case.deleteConfirm"))) return;
    setBusy(true);
    try {
      await deleteOwnCase(id);
      toast.success(t("case.deleted"));
      navigate("/lab");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed");
      setBusy(false);
    }
  };

  // Fase D2: the designer confirms confidentiality before starting work. We record
  // the acceptance (on the designer's own user doc) and then move to "In Progress".
  const handleStartWork = async () => {
    setBusy(true);
    try {
      try {
        await acceptCaseConfidentiality(user.uid, id);
      } catch {
        /* non-blocking: still allow starting */
      }
      await updateStatus(id, "In Progress");
      setConfidentialityOpen(false);
      setConfidentialityChecked(false);
      toast.success(t("actions.statusUpdated"));
      await load();
    } catch (e) {
      toast.error(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const handleChangeDesigner = async () => {
    setBusy(true);
    try {
      await changeDesigner(id);
      toast.success(t("case.designerChanged"));
      await load();
    } catch (e) {
      toast.error(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  // Owner can add or replace CAD files BEFORE the designer starts working
  // (status Published or Designer Selected). Useful e.g. to fix a broken-margin STL.
  const addOrReplaceCad = async (fileList, replace) => {
    const files = Array.from(fileList || []).filter(isCadFile);
    if (!files.length) {
      toast.error(t("cad.invalidType"));
      return;
    }
    setCadBusy(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const u = await uploadFileResumable(f, `cases/${id}/cad`);
        uploaded.push({ url: u.url, name: u.name });
      }
      const current = getCaseCadFiles(caseItem);
      const next = replace ? uploaded : [...current, ...uploaded];
      await updateCase(id, { cadFiles: next, stlUrl: next[0]?.url || "", stlName: next[0]?.name || "" });
      toast.success(replace ? t("cad.replaced") : t("cad.added"));
      setCadIdx(0);
      await load();
    } catch (e) {
      toast.error(e?.message || t("upload.error"));
    } finally {
      setCadBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-6 h-96 w-full rounded-lg" />
      </div>
    );
  }

  if (!caseItem) {
    return <div className="mx-auto max-w-6xl px-4 py-20 text-center text-muted-foreground">Case not found.</div>;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-5 inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:border-brand hover:text-brand"
        data-testid="case-back-button"
      >
        <ArrowLeft className="h-4 w-4" /> {t("common.back")}
      </button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="mb-2 flex items-center gap-3">
            <StatusBadge status={caseItem.status} />
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <User className="h-3.5 w-3.5" /> {t("case.postedBy")}: {caseItem.ownerName}
            </span>
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="case-detail-title">{caseItem.title}</h1>
          {caseItem.caseNumber && (
            <p className="mt-1 font-mono text-sm text-muted-foreground" data-testid="case-detail-number">{caseItem.caseNumber}</p>
          )}
          {caseItem.title && (
            <div className="mt-2 max-w-2xl">
              <TranslatableText text={caseItem.title} context="dental case title" testid="case-title" />
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Main */}
        <div className="min-w-0 space-y-6 lg:col-span-2">
          {(isOwner || isSelectedDesigner) && casePrivate && (casePrivate.patientName || casePrivate.doctorName || casePrivate.modelEngraving) && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-5" data-testid="case-private-info">
              <p className="mb-3 flex items-center gap-1.5 text-sm font-semibold">
                <User className="h-4 w-4 text-slate-500" /> {t("case.patientSection")}
                <span className="ml-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600">{t("case.confidential")}</span>
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {casePrivate.patientName && <DetailItem icon={User} label={t("case.patientName")} value={casePrivate.patientName} />}
                {casePrivate.patientId && <DetailItem icon={User} label={t("case.patientId")} value={casePrivate.patientId} />}
                {casePrivate.doctorName && <DetailItem icon={User} label={t("case.doctorName")} value={casePrivate.doctorName} />}
                {casePrivate.clinicCaseId && <DetailItem icon={Layers} label={t("case.clinicCaseId")} value={casePrivate.clinicCaseId} />}
              </div>
              {casePrivate.modelEngraving && (
                <div className="mt-3 rounded-md border border-slate-200 bg-white p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{t("case.modelEngraving")}</p>
                  <p className="mt-0.5 font-mono text-sm">{casePrivate.modelEngraving}</p>
                </div>
              )}
            </div>
          )}
          {caseItem.photos?.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {caseItem.photos.map((p, i) => (
                <img key={i} src={p} alt="" loading="lazy" decoding="async" className="h-28 w-full rounded-lg border border-border bg-slate-100 object-cover" />
              ))}
            </div>
          )}

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t("cad.viewer3d")}</p>
            {(() => {
              const cadList = getCaseCadFiles(caseItem);
              const active = cadList[Math.min(cadIdx, Math.max(cadList.length - 1, 0))];
              const canManageCad = isOwner && (caseItem.status === "Draft" || caseItem.status === "Published" || caseItem.status === "Designer Selected");
              return (
                <>
                  {cadList.length > 1 && (
                    <div className="mb-2 flex flex-wrap gap-1.5" data-testid="cad-file-tabs">
                      {cadList.map((f, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setCadIdx(i)}
                          className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${i === cadIdx ? "border-brand bg-brand/10 text-brand" : "border-border bg-card text-muted-foreground hover:border-slate-300"}`}
                          data-testid={`cad-file-tab-${i}`}
                        >
                          <Box className="h-3.5 w-3.5" /> <span className="max-w-[140px] truncate">{f.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <StlViewer url={active?.url || null} name={active?.name} />
                  {canDownloadFiles && active?.url && (
                    <div className="mt-2">
                      <Button size="sm" variant="outline" className="rounded-full" disabled={dlBusy === (active.path || active.url)} onClick={() => handleFileDownload(active.path || active.url, active.name)} data-testid="download-stl-button">
                        {dlBusy === (active.path || active.url) ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />} {t("files.downloadStl")}
                      </Button>
                    </div>
                  )}
                  {canManageCad && (
                    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="owner-cad-manage">
                      <span className="text-xs text-muted-foreground">{t("cad.manageHint")}</span>
                      <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium hover:border-brand hover:text-brand">
                        {cadBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} {t("cad.addFile")}
                        <input type="file" accept=".stl,.ply,.obj" multiple className="hidden" disabled={cadBusy} onChange={(e) => { addOrReplaceCad(e.target.files, false); e.target.value = ""; }} data-testid="cad-add-input" />
                      </label>
                      <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100">
                        {cadBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} {t("cad.replaceFile")}
                        <input type="file" accept=".stl,.ply,.obj" multiple className="hidden" disabled={cadBusy} onChange={(e) => { addOrReplaceCad(e.target.files, true); e.target.value = ""; }} data-testid="cad-replace-input" />
                      </label>
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {canDownloadFiles && (() => {
            const cadList = getCaseCadFiles(caseItem);
            const photos = caseItem.photos || [];
            if (!cadList.length && !photos.length) return null;
            const ext = (n) => (String(n || "").split(".").pop() || "file").toUpperCase();
            const fmtSize = (b) => (b ? (b > 1048576 ? (b / 1048576).toFixed(1) + " MB" : Math.max(1, Math.round(b / 1024)) + " KB") : null);
            const photoName = (p) => {
              try { return decodeURIComponent((p.split("/o/")[1] || p).split("?")[0]).split("/").pop(); } catch { return ""; }
            };
            return (
              <div className="rounded-lg border border-border bg-card p-6" data-testid="case-files-section">
                <h3 className="mb-1 flex items-center gap-2 font-heading text-lg font-semibold tracking-tight">
                  <FolderDown className="h-5 w-5 text-brand" /> {t("files.title")}
                </h3>
                <p className="mb-4 text-xs text-muted-foreground">{t("files.hint")}</p>
                <div className="space-y-2">
                  {cadList.map((f, i) => {
                    const ref = f.path || f.url;
                    return (
                      <div key={"cad" + i} className="flex flex-wrap items-center gap-2 rounded-lg border border-border p-3" data-testid={`case-file-cad-${i}`}>
                        <Box className="h-5 w-5 shrink-0 text-brand" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium" data-testid={`case-file-name-${i}`}>{f.name}</p>
                          <p className="text-xs text-muted-foreground">{ext(f.name)} · 3D{fmtSize(f.size) ? ` · ${fmtSize(f.size)}` : ""}</p>
                        </div>
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setCadIdx(i)} data-testid={`case-file-view-${i}`}>
                          <Eye className="mr-1.5 h-3.5 w-3.5" /> {t("files.view")}
                        </Button>
                        <Button size="sm" className="rounded-full bg-brand text-white hover:bg-brand/90" disabled={dlBusy === ref} onClick={() => handleFileDownload(ref, f.name)} data-testid={`case-file-download-${i}`}>
                          {dlBusy === ref ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Download className="mr-1.5 h-3.5 w-3.5" /> {t("files.download")}</>}
                        </Button>
                      </div>
                    );
                  })}
                  {photos.map((p, i) => {
                    const nm = photoName(p) || `photo-${i + 1}`;
                    return (
                      <div key={"ph" + i} className="flex flex-wrap items-center gap-2 rounded-lg border border-border p-3" data-testid={`case-file-photo-${i}`}>
                        <ImageIcon className="h-5 w-5 shrink-0 text-brand" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{nm}</p>
                          <p className="text-xs text-muted-foreground">{t("files.photo")}</p>
                        </div>
                        <a href={p} target="_blank" rel="noreferrer" data-testid={`case-file-photo-view-${i}`}>
                          <Button size="sm" variant="outline" className="rounded-full"><Eye className="mr-1.5 h-3.5 w-3.5" /> {t("files.view")}</Button>
                        </a>
                        <Button size="sm" className="rounded-full bg-brand text-white hover:bg-brand/90" disabled={dlBusy === p} onClick={() => handleFileDownload(p, nm)} data-testid={`case-file-photo-download-${i}`}>
                          {dlBusy === p ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Download className="mr-1.5 h-3.5 w-3.5" /> {t("files.download")}</>}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {(() => {
            const restorations = getCaseRestorations(caseItem);
            const numbering = caseItem.toothNumbering || "fdi";
            if (restorations) {
              return (
                <>
                  <div className="rounded-lg border border-border bg-card p-6">
                    <h3 className="mb-4 font-heading text-lg font-semibold tracking-tight">{t("case.details")}</h3>
                    <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
                      {isOwner ? (
                        <DetailItem icon={DollarSign} label={caseItem.agreedLabPrice ? t("pricing.agreedPrice") : t("case.offeredPrice")} value={(caseItem.agreedLabPrice ?? caseOfferedPrice(caseItem)) ? fmtUSD(caseItem.agreedLabPrice ?? caseOfferedPrice(caseItem)) : "—"} />
                      ) : (
                        <DetailItem icon={DollarSign} label={t("pricing.youWillReceive")} value={caseOfferedPrice(caseItem) ? fmtUSD(caseItem.agreedDesignerNet ?? fromLabPrice(caseOfferedPrice(caseItem), commissionRate).designerNet) : "—"} />
                      )}
                      <DetailItem icon={CalendarClock} label={t("case.deadlineLabel")} value={caseItem.deadline} />
                      <DetailItem icon={Layers} label={t("odontogram.numbering")} value={t(`odontogram.${numbering}`)} />
                    </div>
                    <div className="mt-5 border-t border-border pt-4">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t("restoration.summaryTitle")}</p>
                      <ul className="list-disc space-y-1 pl-5 text-sm text-foreground" data-testid="restorations-summary">
                        {restorations.map((r, i) => (
                          <li key={i} data-testid={`restoration-summary-${i}`}>
                            <span className="font-medium">{t(`case.restorationTypes.${r.type}`, r.type)}</span>
                            {" — "}{t(`case.materials.${r.material}`, r.material)}{r.shade ? ` (${r.shade})` : ""}
                            {" · "}{teethList(r.teeth).map((x) => toothLabel(x.fdi, numbering)).join(", ") || t("restoration.noTeeth")}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="space-y-4" data-testid="restorations-list">
                    {restorations.map((r, i) => (
                      <RestorationCard key={i} restoration={r} index={i} numbering={numbering} />
                    ))}
                  </div>
                </>
              );
            }
            return (
              <div className="rounded-lg border border-border bg-card p-6">
                <h3 className="mb-4 font-heading text-lg font-semibold tracking-tight">{t("case.details")}</h3>
                <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
                  <DetailItem icon={Layers} label={t("case.restorationType")} value={t(`case.restorationTypes.${caseItem.restorationType}`, caseItem.restorationType)} />
                  <DetailItem icon={Layers} label={t("case.material")} value={t(`case.materials.${caseItem.material}`, caseItem.material)} />
                  <DetailItem icon={Layers} label={t("case.toothNumbers")} value={caseItem.toothNumbers} />
                  <DetailItem icon={Layers} label={t("case.shade")} value={caseItem.shade} />
                  {isOwner ? (
                        <DetailItem icon={DollarSign} label={caseItem.agreedLabPrice ? t("pricing.agreedPrice") : t("case.offeredPrice")} value={(caseItem.agreedLabPrice ?? caseOfferedPrice(caseItem)) ? fmtUSD(caseItem.agreedLabPrice ?? caseOfferedPrice(caseItem)) : "—"} />
                      ) : (
                        <DetailItem icon={DollarSign} label={t("pricing.youWillReceive")} value={caseOfferedPrice(caseItem) ? fmtUSD(caseItem.agreedDesignerNet ?? fromLabPrice(caseOfferedPrice(caseItem), commissionRate).designerNet) : "—"} />
                      )}
                  <DetailItem icon={CalendarClock} label={t("case.deadlineLabel")} value={caseItem.deadline} />
                </div>
                {caseItem.clinicalInstructions && (
                  <div className="mt-5 border-t border-border pt-4">
                    <p className="mb-1 text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t("case.instructions")}</p>
                    <TranslatableText text={caseItem.clinicalInstructions} testid="case-instructions" />
                  </div>
                )}
              </div>
            );
          })()}

          {(caseItem.includeModels || caseItem.includeGingiva) && (
            <div className="rounded-lg border border-border bg-card p-6" data-testid="case-additional-deliverables">
              <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold tracking-tight">
                <Box className="h-5 w-5 text-slate-500" /> {t("case.additionalDeliverables")}
              </h3>
              <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
                <div data-testid="deliverable-models">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{t("case.deliverableModels")}</p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-sm font-medium">
                    {caseItem.includeModels
                      ? (<><CheckCircle2 className="h-4 w-4 text-emerald-600" /> {t("common.yes")}</>)
                      : <span className="text-muted-foreground">{t("common.no")}</span>}
                  </p>
                </div>
                <div data-testid="deliverable-gingiva">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{t("case.deliverableGingiva")}</p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-sm font-medium">
                    {caseItem.includeGingiva
                      ? (<><CheckCircle2 className="h-4 w-4 text-emerald-600" /> {t("common.yes")}</>)
                      : <span className="text-muted-foreground">{t("common.no")}</span>}
                  </p>
                </div>
              </div>
            </div>
          )}

          <AiAnalysisPanel ai={caseItem.ai} loading={false} error={false} />

          {(isOwner || isSelectedDesigner) && (
            <SubmissionVersions
              caseItem={caseItem}
              isOwner={isOwner}
              isSelectedDesigner={isSelectedDesigner}
              user={user}
              profile={profile}
              onChanged={load}
            />
          )}

          {(isOwner || isSelectedDesigner) && (
            <CaseReview
              caseItem={caseItem}
              isOwner={isOwner}
              isSelectedDesigner={isSelectedDesigner}
              user={user}
              profile={profile}
            />
          )}

          {(isOwner || isSelectedDesigner) && (
            <CaseComments caseId={id} caseItem={caseItem} canParticipate={isOwner || isSelectedDesigner} user={user} profile={profile} />
          )}
        </div>

        {/* Sidebar */}
        <div className="min-w-0 space-y-6">
          <div className="rounded-lg border border-border bg-card p-6">
            <h3 className="mb-4 font-heading text-base font-semibold tracking-tight">{t("actions.changeStatus")}</h3>
            <CaseStatusTimeline status={caseItem.status} versionCount={counts.versions} changesRequestedCount={counts.changes} />
            {caseItem.paymentStatus === "paid" && caseItem.paymentTransactionId && isOwner && (
              <Link to={`/receipt/${caseItem.paymentTransactionId}`} className="mt-3 block" data-testid="receipt-link">
                <Button variant="outline" size="sm" className="w-full rounded-full">
                  <Receipt className="mr-1.5 h-4 w-4" /> {t("receipt.view")}
                </Button>
              </Link>
            )}
            {caseItem.paymentStatus === "paid" && caseItem.paymentTransactionId && isSelectedDesigner && (
              <Link to={`/payout/${caseItem.paymentTransactionId}`} className="mt-3 block" data-testid="payout-statement-link">
                <Button variant="outline" size="sm" className="w-full rounded-full">
                  <Receipt className="mr-1.5 h-4 w-4" /> {t("payment.viewPayoutStatement")}
                </Button>
              </Link>
            )}
            {caseItem.status === "Completed" && caseItem.paymentStatus === "paid" && (
              payout?.payout_status === "paid" ? (
                <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="designer-payout-paid">
                  <p className="flex items-center gap-1.5 font-semibold">
                    <CheckCircle2 className="h-4 w-4" /> {t("payment.paymentCompleted")} · {t("admin.payoutStatus.paid")}
                  </p>
                  <p className="mt-1 text-xs text-emerald-700/90">
                    {t("payment.payoutPaidHint", {
                      amount: fmtUSD(payout.designer_net),
                      date: payout.payout_paid_at ? new Date(payout.payout_paid_at).toLocaleDateString() : "—",
                    })}
                  </p>
                </div>
              ) : (
                <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="pending-designer-payment">
                  {t("payment.pendingDesignerPayment")}
                </div>
              )
            )}
          </div>

          {/* Owner actions */}
          {isOwner && (
            <div className="rounded-lg border border-border bg-card p-6" data-testid="owner-actions">
              {caseItem.status === "Draft" && (
                <div className="mb-4 space-y-2" data-testid="draft-actions">
                  <Button className="w-full rounded-full bg-brand text-white hover:bg-brand/90" onClick={() => navigate(`/lab/edit/${id}`)} disabled={busy} data-testid="publish-now-button">
                    <Send className="mr-1.5 h-4 w-4" /> {t("case.reviewPublish")}
                  </Button>
                  <Button variant="outline" className="w-full rounded-full" onClick={() => navigate(`/lab/edit/${id}`)} disabled={busy} data-testid="edit-draft-button">
                    <Pencil className="mr-1.5 h-4 w-4" /> {t("case.editDraft")}
                  </Button>
                </div>
              )}
              {caseItem.status === "Designer Selected" && (
                caseItem.paymentStatus === "paid" ? (
                  <div className="mb-4 space-y-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="payment-done-owner">
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
                      <CheckCircle2 className="h-4 w-4" /> {t("payment.paidBadge")} · {fmtUSD(caseItem.agreedLabPrice ?? caseOfferedPrice(caseItem))}
                    </p>
                    <p className="text-xs text-emerald-700/80">{t("payment.paidOwnerHint")}</p>
                  </div>
                ) : (
                  <div className="mb-4 space-y-3" data-testid="payment-panel">
                    <div className="space-y-1.5 rounded-lg border border-brand/30 bg-brand/5 p-3">
                      <p className="text-sm font-semibold">{t("payment.payToActivate")}</p>
                      {(() => {
                        const rate = caseItem.agreedCommissionRate ?? commissionRate;
                        const labPrice = caseItem.agreedLabPrice ?? caseOfferedPrice(caseItem);
                        const calc = fromLabPrice(labPrice, rate);
                        const net = caseItem.agreedDesignerNet ?? calc.designerNet;
                        const commission = caseItem.agreedCommissionAmount ?? calc.commissionAmount;
                        const promoOn = promoResult?.valid;
                        const discount = promoOn ? Math.round(commission * 0.5 * 100) / 100 : 0;
                        const finalFee = Math.round((commission - discount) * 100) / 100;
                        const total = promoOn ? Math.round((net + finalFee) * 100) / 100 : labPrice;
                        return (
                          <div className="space-y-1 text-sm" data-testid="payment-breakdown">
                            <div className="flex justify-between"><span className="text-muted-foreground">{t("promo.designerService")}</span><span data-testid="payment-designer-net">{fmtUSD(net)}</span></div>
                            {promoOn ? (
                              <>
                                <div className="flex justify-between"><span className="text-muted-foreground">{t("promo.regularFee", { rate: Math.round(rate * 100) })}</span><span>{fmtUSD(commission)}</span></div>
                                <div className="flex justify-between text-brand"><span>{t("promo.launchDiscount")} (LAUNCH50)</span><span data-testid="payment-discount">- {fmtUSD(discount)}</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">{t("promo.finalFee")}</span><span data-testid="payment-final-fee">{fmtUSD(finalFee)}</span></div>
                              </>
                            ) : (
                              <div className="flex justify-between"><span className="text-muted-foreground">{t("pricing.platformCommission", { rate: Math.round(rate * 100) })}</span><span>{fmtUSD(commission)}</span></div>
                            )}
                            <div className="flex justify-between border-t border-border pt-1 font-semibold"><span>{t("pricing.totalLabPays")}</span><span data-testid="payment-total">{fmtUSD(total)}</span></div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* LAUNCH50 promo code */}
                    <div className="space-y-2 rounded-lg border border-border p-3" data-testid="promo-panel">
                      {promoResult?.valid ? (
                        <div data-testid="promo-applied">
                          <div className="flex items-center justify-between">
                            <p className="flex items-center gap-1.5 text-sm font-semibold text-brand">
                              <BadgeCheck className="h-4 w-4" /> {t("promo.appliedBanner")}
                            </p>
                            <button className="text-xs text-muted-foreground underline-offset-2 hover:underline" onClick={removePromo} data-testid="promo-remove">{t("promo.remove")}</button>
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground" data-testid="promo-uses">
                            {t("promo.usesLabel", { used: promoResult.uses ?? 0, max: promoResult.max ?? 5 })}
                          </p>
                        </div>
                      ) : (
                        <>
                          <label className="text-xs font-medium text-muted-foreground">{t("promo.label")}</label>
                          <div className="flex gap-2">
                            <Input value={promoInput} onChange={(e) => setPromoInput(e.target.value)} placeholder={t("promo.placeholder")} className="h-9" data-testid="promo-input" />
                            <Button variant="outline" size="sm" className="rounded-full" onClick={applyPromo} disabled={promoBusy || !promoInput.trim()} data-testid="promo-apply">
                              {t("promo.apply")}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                    <Button className="w-full rounded-full bg-brand text-white hover:bg-brand/90" onClick={handlePay} disabled={busy || !caseItem.selectedDesignerId} data-testid="pay-case-button">
                      <DollarSign className="mr-1.5 h-4 w-4" /> {t("payment.payNow")}
                    </Button>
                    <p className="text-[11px] text-muted-foreground">{t("payment.gateHint")}</p>
                    <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="awaiting-start-actions">
                      <p className="text-xs text-amber-800">{t("case.awaitingStartHint")}</p>
                      <Button variant="outline" className="w-full rounded-full" onClick={handleChangeDesigner} disabled={busy} data-testid="change-designer-button">
                        <RefreshCw className="mr-1.5 h-4 w-4" /> {t("case.changeDesigner")}
                      </Button>
                      <Button variant="outline" className="w-full rounded-full" onClick={handleChangeDesigner} disabled={busy} data-testid="republish-case-button">
                        <Send className="mr-1.5 h-4 w-4" /> {t("case.republish")}
                      </Button>
                    </div>
                  </div>
                )
              )}

              <h4 className="mb-3 font-heading text-sm font-semibold tracking-tight">{t("applications.interestedDesigners")}</h4>
              {applications.length >= 2 && (
                <div className="mb-3" data-testid="ai-compare-block">
                  <Button size="sm" className="w-full rounded-full bg-gradient-to-r from-violet-600 to-brand font-semibold text-white hover:opacity-90" onClick={() => setAiCmpIntro(true)} disabled={aiCmpBusy} data-testid="ai-compare-button">
                    {aiCmpBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
                    {aiCmpBusy ? t("aiMatch.comparing") : t("aiMatch.compareCta")}
                  </Button>
                  {Object.keys(aiCmpMap).length > 0 && (
                    <p className="mt-1.5 text-[11px] text-muted-foreground" data-testid="ai-compare-disclaimer">{t("aiMatch.disclaimer")}</p>
                  )}
                  <Dialog open={aiCmpIntro} onOpenChange={setAiCmpIntro}>
                    <DialogContent data-testid="ai-compare-intro-dialog">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-brand" /> {t("aiMatch.compareCta")}</DialogTitle>
                        <DialogDescription className="pt-1 text-left leading-relaxed">{t("aiMatch.introLab")}</DialogDescription>
                      </DialogHeader>
                      <DialogFooter className="gap-2 sm:gap-2">
                        <Button variant="outline" className="rounded-full" onClick={() => setAiCmpIntro(false)} data-testid="ai-compare-intro-cancel">{t("common.cancel")}</Button>
                        <Button className="rounded-full bg-gradient-to-r from-violet-600 to-brand font-semibold text-white hover:opacity-90" onClick={runCompareApplicants} data-testid="ai-compare-intro-confirm">
                          <Sparkles className="mr-1.5 h-4 w-4" /> {t("aiMatch.runLab")}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
              {applications.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="no-applicants">{t("applications.noApplicants")}</p>
              ) : (
                <div className="space-y-3">
                  {[...applications].sort((a, b) => ((aiCmpMap[b.designerId]?.score ?? -1) - (aiCmpMap[a.designerId]?.score ?? -1))).map((a) => {
                    const selected = caseItem.selectedDesignerId === a.designerId;
                    const m = aiCmpMap[a.designerId];
                    const mColor = m ? (m.level === "strong" ? "bg-emerald-600" : m.level === "good" ? "bg-amber-500" : "bg-rose-500") : "";
                    return (
                      <div key={a.designerId} className="rounded-md border border-border p-3" data-testid={`applicant-${a.designerId}`}>
                        {m && (
                          <div className="mb-2 flex flex-wrap items-center gap-2" data-testid={`ai-rank-${a.designerId}`}>
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold text-white ${mColor}`}>
                              <Sparkles className="h-3 w-3" /> #{m.rank} · {m.score}%
                            </span>
                            <span className="text-[11px] text-muted-foreground">{m.reason}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <button type="button" className="text-left text-sm font-semibold text-brand hover:underline" onClick={() => setProfileModal({ designerId: a.designerId, app: a })} data-testid={`applicant-name-${a.designerId}`}>
                              {a.name}
                            </button>
                            <div className="mt-0.5">
                              <RatingSummary
                                avg={applicantRatings[a.designerId]?.avg || 0}
                                count={applicantRatings[a.designerId]?.count || 0}
                                testid={`applicant-rating-${a.designerId}`}
                              />
                            </div>
                          </div>
                          {selected ? (
                            <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">{t("applications.selected")}</Badge>
                          ) : (
                            (caseItem.status === "Published" || caseItem.status === "Designer Selected") && (
                              <Button size="sm" className="rounded-full bg-brand font-semibold text-white hover:bg-brand/90" onClick={() => handleSelect(a)} disabled={busy} data-testid={`select-${a.designerId}`}>
                                {t("applications.select")}
                              </Button>
                            )
                          )}
                        </div>
                        <button type="button" className="mt-1 text-xs text-muted-foreground underline-offset-2 hover:underline" onClick={() => setProfileModal({ designerId: a.designerId, app: a })} data-testid={`view-profile-${a.designerId}`}>
                          {t("applications.viewProfile")}
                        </button>
                        {(() => {
                          const offered = caseOfferedPrice(caseItem);
                          const p = a.labPrice ? { labPrice: a.labPrice, designerNet: a.designerNet } : fromLabPrice(offered, commissionRate);
                          return (
                            <div className="mt-2 grid grid-cols-2 gap-2 rounded-md bg-secondary/50 p-2" data-testid={`applicant-pricing-${a.designerId}`}>
                              <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t("pricing.labPays")}</p>
                                <p className="text-sm font-bold" data-testid={`applicant-lab-pays-${a.designerId}`}>{fmtUSD(p.labPrice)}</p>
                              </div>
                              <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t("pricing.designerReceives")}</p>
                                <p className="text-sm font-bold text-emerald-700" data-testid={`applicant-designer-net-${a.designerId}`}>{fmtUSD(p.designerNet)}</p>
                              </div>
                              {a.offerType === "counter" && (
                                <span className="col-span-2 text-[10px] font-medium text-amber-600">{t("pricing.requestDifferent")}</span>
                              )}
                            </div>
                          );
                        })()}
                        {a.message && <p className="mt-1 text-xs text-muted-foreground">{a.message}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
              {canDeleteOwnCase && (
                <div className="mt-4 border-t border-border pt-4">
                  <Button variant="ghost" className="w-full rounded-full text-red-600 hover:bg-red-50 hover:text-red-700" onClick={handleDelete} disabled={busy} data-testid="delete-case-button">
                    <Trash2 className="mr-1.5 h-4 w-4" /> {t("case.deleteCase")}
                  </Button>
                  <p className="mt-1 text-center text-[11px] text-muted-foreground">{t("case.deleteHint")}</p>
                </div>
              )}
            </div>
          )}

          {/* Designer actions */}
          {!isOwner && profile?.role === "designer" && (
            <div className="rounded-lg border border-border bg-card p-6" data-testid="designer-actions">
              {caseItem.status === "Published" && !myApp && (
                <Dialog open={applyOpen} onOpenChange={(o) => {
                  if (o && !ensurePayoutPolicy()) return; // gate: accept payout policy first
                  setApplyOpen(o); if (!o) { setCounterOn(false); setDesiredNet(""); }
                }}>
                  <DialogTrigger asChild>
                    <Button className="w-full rounded-full bg-brand text-white hover:bg-brand/90" data-testid="apply-button">
                      {t("applications.apply")}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>{t("applications.apply")}</DialogTitle></DialogHeader>
                    {(() => {
                      const pct = Math.round(commissionRate * 100);
                      const offered = caseOfferedPrice(caseItem);
                      const accept = fromLabPrice(offered, commissionRate);
                      const net = Number(desiredNet);
                      const counter = net > 0 ? fromDesiredNet(net, commissionRate) : null;
                      return (
                        <div className="space-y-3">
                          {!counterOn ? (
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="apply-net-panel">
                              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">{t("pricing.youWillReceive")}</p>
                              <p className="font-heading text-2xl font-bold text-emerald-700" data-testid="apply-designer-net">{fmtUSD(accept.designerNet)}</p>
                              <p className="text-[11px] text-emerald-700/80">{t("pricing.feeDeducted", { rate: pct })}</p>
                              <button type="button" className="mt-2 text-xs font-medium text-brand hover:underline" onClick={() => setCounterOn(true)} data-testid="request-different-price">
                                {t("pricing.requestDifferent")}
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-2 rounded-lg border border-border bg-secondary/40 p-3" data-testid="counter-panel">
                              <label className="text-sm font-medium">{t("pricing.desiredNet")} (USD)</label>
                              <Input type="number" min="0" value={desiredNet} onChange={(e) => setDesiredNet(e.target.value)} placeholder="e.g. 35" data-testid="counter-desired-net" />
                              {counter && (
                                <div className="space-y-1 border-t border-border pt-2 text-sm" data-testid="counter-breakdown">
                                  <div className="flex justify-between"><span className="text-muted-foreground">{t("pricing.youWillReceive")}</span><span className="font-semibold text-emerald-700">{fmtUSD(counter.designerNet)}</span></div>
                                  <div className="flex justify-between"><span className="text-muted-foreground">{t("pricing.platformCommission", { rate: pct })}</span><span>{fmtUSD(counter.commissionAmount)}</span></div>
                                  <div className="flex justify-between font-semibold"><span>{t("pricing.labWillPay")}</span><span data-testid="counter-lab-pays">{fmtUSD(counter.labPrice)}</span></div>
                                </div>
                              )}
                              <p className="text-[11px] text-muted-foreground">{t("pricing.counterInfo")}</p>
                              <button type="button" className="text-xs font-medium text-brand hover:underline" onClick={() => { setCounterOn(false); setDesiredNet(""); }} data-testid="cancel-counter">
                                {t("pricing.acceptOffer")}
                              </button>
                            </div>
                          )}
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium">{t("applications.yourMessage")}</label>
                            <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("applications.messagePlaceholder")} data-testid="apply-message" />
                          </div>
                        </div>
                      );
                    })()}
                    <DialogFooter>
                      <Button onClick={handleApply} disabled={busy} className="rounded-full bg-brand text-white hover:bg-brand/90" data-testid="send-application">
                        {t("applications.sendApplication")}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}

              {myApp && !isSelectedDesigner && (
                <div className="space-y-2" data-testid="already-applied">
                  <div className="flex items-center gap-2 rounded-md bg-secondary p-3 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" /> {t("applications.alreadyApplied")}
                  </div>
                  {myApp.designerNet != null && (
                    <div className="rounded-md bg-emerald-50 p-3 text-sm">
                      <span className="text-emerald-700/80">{t("pricing.youWillReceive")}: </span>
                      <span className="font-bold text-emerald-700" data-testid="my-app-net">{fmtUSD(myApp.designerNet)}</span>
                    </div>
                  )}
                </div>
              )}

              {isSelectedDesigner && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 rounded-md bg-emerald-50 p-3 text-sm text-emerald-700">
                    <CheckCircle2 className="h-4 w-4" /> {t("applications.selected")}
                  </div>
                  {caseItem.status === "Designer Selected" && (
                    caseItem.paymentStatus === "paid" ? (
                      <Button className="w-full rounded-full bg-amber-600 text-white hover:bg-amber-700" onClick={() => { setConfidentialityChecked(false); setConfidentialityOpen(true); }} disabled={busy} data-testid="start-progress-button">
                        <PlayCircle className="mr-1.5 h-4 w-4" /> {t("actions.startProgress")}
                      </Button>
                    ) : (
                      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="awaiting-payment-designer">
                        {t("payment.awaitingPaymentDesigner")}
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {profileModal && (
        <DesignerProfileModal
          designerId={profileModal.designerId}
          open={!!profileModal}
          onOpenChange={(v) => !v && setProfileModal(null)}
          canSelect={isOwner && (caseItem.status === "Published" || caseItem.status === "Designer Selected") && caseItem.selectedDesignerId !== profileModal.designerId}
          selecting={busy}
          onSelect={async () => { await handleSelect(profileModal.app); setProfileModal(null); }}
        />
      )}

      <Dialog open={confidentialityOpen} onOpenChange={setConfidentialityOpen}>
        <DialogContent data-testid="confidentiality-dialog">
          <DialogHeader>
            <DialogTitle>{t("confidentiality.title")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t("confidentiality.body")}</p>
          <label className="flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 bg-slate-50/60 p-3 text-sm" data-testid="confidentiality-checkbox-label">
            <input type="checkbox" className="mt-0.5 h-4 w-4 rounded border-slate-300" checked={confidentialityChecked} onChange={(e) => setConfidentialityChecked(e.target.checked)} data-testid="confidentiality-checkbox" />
            <span>{t("confidentiality.checkbox")}</span>
          </label>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setConfidentialityOpen(false)} data-testid="confidentiality-cancel">
              {t("common.cancel")}
            </Button>
            <Button className="rounded-full bg-amber-600 text-white hover:bg-amber-700" onClick={handleStartWork} disabled={busy || !confidentialityChecked} data-testid="confidentiality-confirm">
              <PlayCircle className="mr-1.5 h-4 w-4" /> {t("confidentiality.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PayoutPolicyDialog
        open={payoutGateOpen}
        onOpenChange={setPayoutGateOpen}
        mode="accept"
        onAccept={acceptPayoutGate}
        busy={payoutGateBusy}
      />
    </div>
  );
}
